import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useReopenTicket } from 'hooks/api/useTickets';

type UseTicketReopenPopupT = {
  isReopeningTicket: boolean;
  isTicketSuccessfullyReopened: boolean;
  isTicketReopeningError: boolean;
  reopenTicketErrorMessage: string | undefined;
  reopenTicketValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleReopenTicket: (ticketIds: string[]) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketReopenPopup = (): UseTicketReopenPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const reopenTicketMutation = useReopenTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });

  /**
   * Variables
   */
  const reopenTicketErrorMessage = useMemo(
    () => getErrorMessage(reopenTicketMutation.error),
    [reopenTicketMutation.error]
  );
  const reopenTicketValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(reopenTicketMutation.error),
    [reopenTicketMutation.error]
  );

  /**
   * Handlers
   */
  const resetReopenTicketMutationState = useCallback(() => {
    if (reopenTicketMutation.isSuccess || reopenTicketMutation.isError) {
      reopenTicketMutation.reset();
    }
  }, [reopenTicketMutation]);
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetReopenTicketMutationState();
    },
    [resetReopenTicketMutationState]
  );
  const handleReopenTicket = useCallback(
    (ticketIds: string[]) => () => {
      reopenTicketMutation.mutate({ ticketIds });
    },
    [reopenTicketMutation]
  );

  return {
    isReopeningTicket: reopenTicketMutation.isLoading,
    isTicketSuccessfullyReopened: reopenTicketMutation.isSuccess,
    isTicketReopeningError: reopenTicketMutation.isError,
    reopenTicketErrorMessage,
    reopenTicketValidationErrorMessage,
    showPopupContent: reopenTicketMutation.isIdle || reopenTicketMutation.isLoading,
    handleReopenTicket,
    handleClosePopup,
  };
};

export default useTicketReopenPopup;
